import * as React from 'react';
// Import hooks.
import {
    useAddressVerification,
    UseAddressVerificationResults,
} from '@app/hooks/view';
import {
    UseInputAddressState,
    useInputDropdown,
    UseInputDropdownResults,
    UseInputFormResult,
    useInputPhone,
    UseInputPhoneResults,
    useInputText,
    UseInputTextResults,
    useInputYesNo,
    UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import props.
import {
   FormProducerSignupProps,
} from '@app/props';
// Import api models.
import {
    Address,
    PossibleBusinessType,
    PossibleMasterProducer,
} from '@app/models/api';

import DropdownItem from 'Models/AppModels/DropdownItem';

interface UseSubformProducerCompanyDetailsParameters {
    id: string;
    showValidations?: boolean;
    props: FormProducerSignupProps;
    finishConfirmCompanyAddressVerification: (address: Address) => void;
    finishConfirmMailingAddressVerification: (address: Address) => void;
}

interface UseSubformProducerCompanyDetailsResults {
    companyAddressVerification: UseAddressVerificationResults;
    mailingAddressVerification: UseAddressVerificationResults;
    companyNameInput: UseInputTextResults;
    faxInput: UseInputPhoneResults;
    isAssociatedWithAnotherProducerInput: UseInputYesNoResults;
    isMailingAddressInput: UseInputYesNoResults;
    nationalProducerLicenseNumberInput: UseInputTextResults;
    phoneInput: UseInputPhoneResults;
    producerAssociationInput: UseInputDropdownResults;
    businessTypeInput: UseInputDropdownResults;
    residentInsuranceLicenseNumberInput: UseInputTextResults;
    taxIdInput: UseInputTextResults;
    externalAgencyIdInput: UseInputTextResults;
    isExternalAgencyIdRequired: boolean;
    flagAllInputsToShowValidations: () => void;
    checkFormValidationWithFocus: () => boolean;
    isCompanyAddressMailingAddress: () => boolean;
    isAssociatedWithAnotherProducer: () => boolean;
}

const useSubformProducerCompanyDetails = ( params: UseSubformProducerCompanyDetailsParameters ): UseSubformProducerCompanyDetailsResults => {
    
	// #region exported methods

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
        companyAddressVerification.addressLocationInput.changeShowValidationFlag(true);
        mailingAddressVerification.addressLocationInput.changeShowValidationFlag(true);
        companyNameInput.changeShowValidationFlag(true);
        faxInput.changeShowValidationFlag(true);
        isAssociatedWithAnotherProducerInput.changeShowValidationFlag(true);
        isMailingAddressInput.changeShowValidationFlag(true);
        nationalProducerLicenseNumberInput.changeShowValidationFlag(true);
        phoneInput.changeShowValidationFlag(true);
        producerAssociationInput.changeShowValidationFlag(true);
        businessTypeInput.changeShowValidationFlag(true);
        residentInsuranceLicenseNumberInput.changeShowValidationFlag(true);
        taxIdInput.changeShowValidationFlag(true);
        externalAgencyIdInput.changeShowValidationFlag(true);
    };

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidationWithFocus = (): boolean => {
        return checkInputValidationWithFocus<string>(companyNameInput)
            && checkInputValidationWithFocus<string>(businessTypeInput)
            && checkInputValidationWithFocus<UseInputAddressState>(companyAddressVerification.addressLocationInput)
            && checkInputValidationWithFocus<boolean | undefined>(isMailingAddressInput)
            && checkInputValidationWithFocus<UseInputAddressState>(mailingAddressVerification.addressLocationInput)
            && checkInputValidationWithFocus<string>(phoneInput)
            && checkInputValidationWithFocus<string>(faxInput)
            && checkInputValidationWithFocus<string>(nationalProducerLicenseNumberInput)
            && checkInputValidationWithFocus<string>(residentInsuranceLicenseNumberInput)
            && checkInputValidationWithFocus<string>(taxIdInput)
            && checkInputValidationWithFocus<boolean | undefined>(isAssociatedWithAnotherProducerInput)
            && checkInputValidationWithFocus<string>(producerAssociationInput)
            && checkInputValidationWithFocus<string>(externalAgencyIdInput);
    };

    // #endregion exported methods

    // #region private methods

    /**
     * Validates an individual input with focus.
     * @param input
     */
    const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
        const isValid = input.isInputValid;
        if (!isValid) {
            input.focusToInput();
        }
        return isValid;
    };

    const isAssociatedWithAnotherProducer = (): boolean => {
        return isAssociatedWithAnotherProducerInput.value ?? false;
    }

    const isCompanyAddressMailingAddress = (): boolean => {
        return isMailingAddressInput.value ?? true;
    }

    //#region id generators

    const generateIdForForm = (): string => {
        return params.id;
    };

    const generateIdForCompanyAddress = (): string => {
        return `${generateIdForForm()}-companyAddress`;
    };

    const generateIdForMailingAddress = (): string => {
        return `${generateIdForForm()}-mailingAddress`;
    };

    const generateIdForCompanyName = (): string => {
        return `${generateIdForForm()}-companyName`;
    };

    const generateIdForBusinessTypeInput = (): string => {
        return `${generateIdForForm()}-businessType`;
    };

    const generateIdForFaxInput = (): string => {
        return `${generateIdForForm()}-fax`;
    };

    const generateIdForPhoneInput = (): string => {
        return `${generateIdForForm()}-phone`;
    };

    const generateIdForIsAssociatedWithAnotherProducerInput = (): string => {
        return `${generateIdForForm()}-isAssociatedWithAnotherProducer`;
    };

    const generateIdForIsMailingAddressInput = (): string => {
        return `${generateIdForForm()}-isMailingAddress`;
    };

    const generateIdForNationalProducerLicenseInput = (): string => {
        return `${generateIdForForm()}-nationalProducerLicense`;
    };

    const generateIdForResidentInsuranceLicenseNumberInput = (): string => {
        return `${generateIdForForm()}-residentInsuranceLicenseNumber`;
    };

    const generateIdForTaxIdInput = (): string => {
        return `${generateIdForForm()}-taxId`;
    };

    const generateIdForProducerAssociationInput = (): string => {
        return `${generateIdForForm()}-producerAssociation`;
    };

    const generateIdForExternalAgencyIdInput = (): string => {
        return `${generateIdForForm}-externalAgencyId`;
    };

    //#endregion id generators

    const getDefaultBusinessTypeDropdownItem = (): DropdownItem => {
        return new DropdownItem({
            label: '--Please select a business type--',
            value: '0',
        });
    };

    const getBusinessTypeItems = (): DropdownItem[] => {
        return params.props.possibleBusinessTypes.map(
            (possibleBusinessType: PossibleBusinessType) => {
                return new DropdownItem({
                    label: possibleBusinessType.displayName !== null ? possibleBusinessType.displayName : String.empty(),
                    value: possibleBusinessType.id !== null ? possibleBusinessType.id.toString() : '0',
                });
            }
        );
    };

    const getDefaultProducerAssociationDropdownItem = (): DropdownItem => {
        return new DropdownItem({
            label: '--Select producer--',
            value: String.empty(),
        });
    };
    
    const getProducerAssociationItems = (): DropdownItem[] => {
        return params.props.possibleMasterProducers.map(
            (possibleMasterProducer: PossibleMasterProducer) => {
                return new DropdownItem({
                    label: possibleMasterProducer.displayName !== null ? possibleMasterProducer.displayName : String.empty(),
                    value: possibleMasterProducer.id !== null ? possibleMasterProducer.id.toString() : '0',
                });
            }
        );
    };

    // #endregion private methods

    // #region variables

    // Instantiate the 'company address verification' hook.
    const companyAddressVerification: UseAddressVerificationResults = useAddressVerification({
        addressLocationInputParameters: {
            addressInputParameters: {
                id: generateIdForCompanyAddress(),
                label: 'Enter Company Address, City, State, Zip',
                showValidation: params.showValidations,
                required: true,
                disabled: false,
                initialInput: {
                    address: undefined,
                    addressSingleLine: String.empty(),
                },
                possibleStates: params.props.possibleStates,
                onGetAddressSuggestions: params.props.addressProps.getAddressSuggestions,
            },
        },
        finishConfirmAddressVerification: params.finishConfirmCompanyAddressVerification,
        possibleStates: params.props.possibleStates,
        ignorePossibleStateRiskAllowed: true,
        initialInput: undefined,
        verifySingleLineAddress: params.props.addressProps.verifySingleLineAddress,
        verifyAddress: params.props.addressProps.verifyAddress,
    });

   
    const companyNameInput: UseInputTextResults = useInputText({
        id: generateIdForCompanyName(),
        label: 'Company Name',
        showValidation: params.showValidations,
        required: true,
        disabled: false,
        value: String.empty(),
    });

    
    const nationalProducerLicenseNumberInput: UseInputTextResults = useInputText({
        id: generateIdForNationalProducerLicenseInput(),
        label: 'National Producer License Number (NPN)',
        showValidation: params.showValidations,
        required: true,
        disabled: false,
        value: String.empty(),
    });

    const residentInsuranceLicenseNumberInput: UseInputTextResults = useInputText({
        id: generateIdForResidentInsuranceLicenseNumberInput(),
        label: 'Resident Insurance License Number',
        showValidation: params.showValidations,
        required: true,
        disabled: false,
        value: String.empty(),
    });

    const taxIdInput: UseInputTextResults = useInputText({
        id: generateIdForTaxIdInput(),
        label: 'Tax Id',
        showValidation: params.showValidations,
        required: true,
        disabled: false,
        value: String.empty(),
    });

    const phoneInput: UseInputPhoneResults = useInputPhone({
        id: generateIdForPhoneInput(),
        label: 'Phone',
        showValidation: params.showValidations,
        required: true,
        disabled: false,
        value: String.empty(),
    });

    const faxInput: UseInputPhoneResults = useInputPhone({
        id: generateIdForFaxInput(),
        label: 'Fax',
        showValidation: params.showValidations,
        required: false,
        disabled: false,
        value: String.empty(),
    });

    const isMailingAddressInput: UseInputYesNoResults = useInputYesNo({
        id: generateIdForIsMailingAddressInput(),
        label: 'Is the company address above also your mailing address?',
        showValidation: params.showValidations,
        required: false,
        disabled: false,
        isYesChecked: true,
    });

    const mailingAddressVerification: UseAddressVerificationResults = useAddressVerification({
        addressLocationInputParameters: {
            addressInputParameters: {
                id: generateIdForMailingAddress(),
                label: 'Enter Mailing Address, City, State, Zip',
                showValidation: params.showValidations,
                required: true,
                disabled: isCompanyAddressMailingAddress(),
                initialInput: {
                    address: undefined,
                    addressSingleLine: String.empty(),
                },
                possibleStates: params.props.possibleStates,
                onGetAddressSuggestions: params.props.addressProps.getAddressSuggestions,
            },
        },
        finishConfirmAddressVerification: params.finishConfirmMailingAddressVerification,
        possibleStates: params.props.possibleStates,
        ignorePossibleStateRiskAllowed: true,
        initialInput: undefined,
        verifySingleLineAddress: params.props.addressProps.verifySingleLineAddress,
        verifyAddress: params.props.addressProps.verifyAddress,
    });


    const businessTypeInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForBusinessTypeInput(),
        label: 'Business Type',
        required: true,
        disabled: false,
        value: String.empty(),
        defaultDropdownItem: getDefaultBusinessTypeDropdownItem(),
        dropdownItems: getBusinessTypeItems(),
        showValidation: params.showValidations,
    });

    const doesProvidedMasterProducerIdExist: boolean = React.useMemo(
        () => {
            if (params.props.masterProducerId !== undefined) {
                return params.props.possibleMasterProducers.some(
                    (masterProducer: PossibleMasterProducer) => masterProducer.id === params.props.masterProducerId
                )
            }
            return false;
        },
        [params.props.masterProducerId, params.props.possibleMasterProducers]
    );


    const isAssociatedWithAnotherProducerInput: UseInputYesNoResults = useInputYesNo({
        id: generateIdForIsAssociatedWithAnotherProducerInput(),
        label: 'Will you be associated with another producer?',
        showValidation: params.showValidations,
        required: true,
        disabled: doesProvidedMasterProducerIdExist,
        isYesChecked: doesProvidedMasterProducerIdExist,
    });

    const producerAssociationInput: UseInputDropdownResults = useInputDropdown({
        id: generateIdForProducerAssociationInput(),
        label: 'Producer Association',
        required: true,
        disabled: !isAssociatedWithAnotherProducer() || doesProvidedMasterProducerIdExist,
        value: params.props.masterProducerId !== undefined && doesProvidedMasterProducerIdExist ? params.props.masterProducerId.toString() : String.empty(),
        defaultDropdownItem: getDefaultProducerAssociationDropdownItem(),
        dropdownItems: getProducerAssociationItems(),
        showValidation: params.showValidations,
    });

    const matchingProducerAssociation: PossibleMasterProducer | undefined = React.useMemo(() => {
        return params.props.possibleMasterProducers.find((possibleMasterProducer: PossibleMasterProducer) => {
            return possibleMasterProducer.id !== null && possibleMasterProducer.id?.toString() === producerAssociationInput.value;
        });
    }, [producerAssociationInput.value]);

    const isExternalAgencyIdRequired: boolean = React.useMemo(
        () => matchingProducerAssociation?.externalAgencyIdProperties?.required ?? false,
        [matchingProducerAssociation]);

    const externalAgencyIdInputLabel: string | undefined = React.useMemo(
        () => matchingProducerAssociation?.externalAgencyIdProperties?.label ?? undefined,
        [matchingProducerAssociation]);

    const externalAgencyIdInput: UseInputTextResults = useInputText({
        id: generateIdForExternalAgencyIdInput(),
        label: externalAgencyIdInputLabel ?? 'External Agency Id',
        showValidation: params.showValidations,
        required: true,
        disabled: !isExternalAgencyIdRequired,
        value: String.empty(),
    });

    // #endregion variables

    // #region life-cycle methods.

    React.useEffect(() => {
        const selectedProducerAssociation: string | undefined = producerAssociationInput.value;
        if (selectedProducerAssociation !== undefined && !isAssociatedWithAnotherProducerInput.value) {
            producerAssociationInput.setInput(String.empty());
            externalAgencyIdInput.setInput(String.empty());
        }
    }, [isAssociatedWithAnotherProducerInput.value]);

    React.useEffect(() => {
        if (!isExternalAgencyIdRequired) {
            externalAgencyIdInput.setInput(String.empty());
        }
    }, [isExternalAgencyIdRequired]);

    // #endregion life-cycle methods.

    return {
        companyAddressVerification,
        mailingAddressVerification,
        companyNameInput,
        businessTypeInput,
        isMailingAddressInput,
        phoneInput,
        faxInput,
        nationalProducerLicenseNumberInput,
        residentInsuranceLicenseNumberInput,
        taxIdInput,
        isAssociatedWithAnotherProducerInput,
        producerAssociationInput,
        externalAgencyIdInput,
        isExternalAgencyIdRequired,
        flagAllInputsToShowValidations,
        checkFormValidationWithFocus,
        isAssociatedWithAnotherProducer,
        isCompanyAddressMailingAddress
    };
};

// Export parameters.
export { UseSubformProducerCompanyDetailsParameters };

// Export results.
export { UseSubformProducerCompanyDetailsResults };

export default useSubformProducerCompanyDetails;
